import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ordersApi, storesApi } from '../services/api';

const fmtMoney = (value) => `₦${Number(value || 0).toLocaleString()}`;
const fmtDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const datePart = date.toLocaleDateString('en-US', {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
    return `${datePart} : ${timePart}`;
};

function getPaymentProof(order) {
    return order?.metadata?.bankTransferProof || order?.metadata?.bank_transfer_proof || null;
}

export default function OrdersManagementPage() {
    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [paymentFilter, setPaymentFilter] = useState('all');
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const loadStores = useCallback(async () => {
        const storesRes = await storesApi.list({ limit: 300 });
        const storeRows = storesRes?.data?.data || [];
        setStores(storeRows);
        if (!selectedStoreId && storeRows.length > 0) {
            setSelectedStoreId(String(storeRows[0].id));
        }
    }, [selectedStoreId]);

    const loadOrders = useCallback(async (storeId) => {
        if (!storeId) return;
        setLoading(true);
        const response = await ordersApi.listByStore(storeId, {
            limit: 200,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            payment_status: paymentFilter !== 'all' ? paymentFilter : undefined,
        });
        if (!response.success) {
            window.alert(response.error || 'Could not load orders');
            setOrders([]);
            setLoading(false);
            return;
        }

        setOrders(response.data || []);

        if (selectedOrderId) {
            const stillExists = (response.data || []).some((row) => Number(row.id) === Number(selectedOrderId));
            if (!stillExists) {
                setSelectedOrderId(null);
                setSelectedOrder(null);
            }
        }
        setLoading(false);
    }, [paymentFilter, selectedOrderId, statusFilter]);

    useEffect(() => {
        loadStores();
    }, [loadStores]);

    useEffect(() => {
        if (selectedStoreId) loadOrders(selectedStoreId);
    }, [selectedStoreId, loadOrders]);

    const selectedStore = useMemo(
        () => stores.find((item) => String(item.id) === String(selectedStoreId)),
        [stores, selectedStoreId]
    );

    const openOrderDetails = async (orderId) => {
        setSelectedOrderId(orderId);
        setDetailsLoading(true);
        const response = await ordersApi.getById(orderId);
        if (!response.success) {
            window.alert(response.error || 'Could not load order details');
            setDetailsLoading(false);
            return;
        }
        setSelectedOrder(response.data || null);
        setDetailsLoading(false);
    };

    const markPaymentReceived = async (order) => {
        const response = await ordersApi.markPaymentReceived(order.id, order.payment_reference || '');
        if (!response.success) {
            window.alert(response.error || 'Could not mark payment as received');
            return;
        }
        if (selectedOrderId === order.id) {
            openOrderDetails(order.id);
        }
        loadOrders(selectedStoreId);
    };

    const selectedProof = getPaymentProof(selectedOrder);

    const markCompleted = async (order) => {
        const response = await ordersApi.updateStatus(order.id, 'completed');
        if (!response.success) {
            window.alert(response.error || 'Could not mark order as completed');
            return;
        }
        if (selectedOrderId === order.id) {
            openOrderDetails(order.id);
        }
        loadOrders(selectedStoreId);
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Orders</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Orders</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={() => loadOrders(selectedStoreId)}>↻</button>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="panel stock-panel">
                    <div className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))' }}>
                        <label>
                            <span>Store</span>
                            <select value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)}>
                                {stores.map((store) => (
                                    <option key={store.id} value={store.id}>{store.name}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Order Status</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">All</option>
                                <option value="pending">Pending</option>
                                <option value="processing">Processing</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </label>
                        <label>
                            <span>Payment Status</span>
                            <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
                                <option value="all">All</option>
                                <option value="pending">Pending</option>
                                <option value="processing">Processing</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                            </select>
                        </label>
                    </div>
                    <p className="muted-sm" style={{ marginTop: 10 }}>Viewing: {selectedStore?.name || '-'}</p>
                </div>

                <div className="panel stock-panel">
                    <h3>Order List</h3>
                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Order</th>
                                    <th>Customer</th>
                                    <th>Payment</th>
                                    <th>Status</th>
                                    <th>Total</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={7} className="dash-empty-row">Loading...</td></tr>
                                ) : orders.length === 0 ? (
                                    <tr><td colSpan={7} className="dash-empty-row">No orders found</td></tr>
                                ) : orders.map((order) => (
                                    <tr
                                        key={order.id}
                                        style={{ cursor: 'pointer', background: Number(selectedOrderId) === Number(order.id) ? '#fff7ed' : undefined }}
                                        onClick={() => openOrderDetails(order.id)}
                                    >
                                        <td>#{order.id}</td>
                                        <td>
                                            <strong>{order.customer_name || '-'}</strong>
                                            <div className="muted-cell">{order.customer_email || '-'}</div>
                                        </td>
                                        <td>
                                            <div>{order.payment_method || '-'}</div>
                                            <div className="muted-cell">{order.payment_status || '-'}</div>
                                        </td>
                                        <td>{order.status || '-'}</td>
                                        <td>{fmtMoney(order.total_amount)}</td>
                                        <td>{fmtDateTime(order.created_at)}</td>
                                        <td style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <button className="btn btn-outline btn-sm" type="button" onClick={(e) => { e.stopPropagation(); openOrderDetails(order.id); }}>
                                                View Details
                                            </button>
                                            {order.payment_status !== 'completed' ? (
                                                <button className="btn btn-sm" type="button" onClick={(e) => { e.stopPropagation(); markPaymentReceived(order); }}>
                                                    Mark Payment Received
                                                </button>
                                            ) : null}
                                            {!['delivered', 'cancelled', 'refunded'].includes(String(order.status || '').toLowerCase()) ? (
                                                <button className="btn btn-outline btn-sm" type="button" onClick={(e) => { e.stopPropagation(); markCompleted(order); }}>
                                                    Mark Completed
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="panel stock-panel">
                    <h3>Order Details</h3>
                    {!selectedOrderId ? (
                        <p className="muted-sm">Click an order to view full details.</p>
                    ) : detailsLoading ? (
                        <p className="muted-sm">Loading details...</p>
                    ) : !selectedOrder ? (
                        <p className="muted-sm">Order details not available.</p>
                    ) : (
                        <div style={{ display: 'grid', gap: 14 }}>
                            <div className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))' }}>
                                <label>
                                    <span>Order ID</span>
                                    <input value={`#${selectedOrder.id}`} readOnly />
                                </label>
                                <label>
                                    <span>Store ID</span>
                                    <input value={selectedOrder.store_id || '-'} readOnly />
                                </label>
                                <label>
                                    <span>Created At</span>
                                    <input value={fmtDateTime(selectedOrder.created_at)} readOnly />
                                </label>
                                <label>
                                    <span>Payment Method</span>
                                    <input value={selectedOrder.payment_method || '-'} readOnly />
                                </label>
                                <label>
                                    <span>Payment Status</span>
                                    <input value={selectedOrder.payment_status || '-'} readOnly />
                                </label>
                                <label>
                                    <span>Order Status</span>
                                    <input value={selectedOrder.status || '-'} readOnly />
                                </label>
                                <label>
                                    <span>Customer Name</span>
                                    <input value={selectedOrder.customer_name || '-'} readOnly />
                                </label>
                                <label>
                                    <span>Customer Email</span>
                                    <input value={selectedOrder.customer_email || '-'} readOnly />
                                </label>
                                <label>
                                    <span>Customer Phone</span>
                                    <input value={selectedOrder.customer_phone || '-'} readOnly />
                                </label>
                            </div>

                            <div className="products-table-wrap">
                                <table className="products-table admin-table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Qty</th>
                                            <th>Unit Price</th>
                                            <th>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selectedOrder.items || []).length === 0 ? (
                                            <tr><td colSpan={4} className="dash-empty-row">No items in order</td></tr>
                                        ) : (selectedOrder.items || []).map((item, idx) => (
                                            <tr key={`${item.product_id || item.productId || idx}-${idx}`}>
                                                <td>{item.name || `Product #${item.product_id || item.productId || '-'}`}</td>
                                                <td>{Number(item.quantity || 0)}</td>
                                                <td>{fmtMoney(item.price || 0)}</td>
                                                <td>{fmtMoney(item.subtotal || (Number(item.price || 0) * Number(item.quantity || 0)))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {selectedProof?.attached ? (
                                <div className="order-proof-panel">
                                    <div>
                                        <h4>Customer Payment Receipt</h4>
                                        <p>{selectedProof.mimeType || selectedProof.kind || 'Uploaded proof'} {selectedProof.uploadedAt ? `• ${fmtDateTime(selectedProof.uploadedAt)}` : ''}</p>
                                    </div>
                                    {selectedProof.dataUrl && String(selectedProof.mimeType || '').startsWith('image/') ? (
                                        <a href={selectedProof.dataUrl} target="_blank" rel="noreferrer" className="order-proof-image-link">
                                            <img src={selectedProof.dataUrl} alt="Customer payment receipt" />
                                        </a>
                                    ) : selectedProof.dataUrl ? (
                                        <a className="btn btn-outline btn-sm" href={selectedProof.dataUrl} target="_blank" rel="noreferrer">Open Receipt</a>
                                    ) : selectedProof.url ? (
                                        <a className="btn btn-outline btn-sm" href={selectedProof.url} target="_blank" rel="noreferrer">Open Receipt</a>
                                    ) : (
                                        <span className="muted-sm">Receipt was uploaded, but no preview data is available for this older order.</span>
                                    )}
                                </div>
                            ) : null}

                            <div className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))' }}>
                                <label>
                                    <span>Subtotal</span>
                                    <input value={fmtMoney(selectedOrder.subtotal)} readOnly />
                                </label>
                                <label>
                                    <span>Tax</span>
                                    <input value={fmtMoney(selectedOrder.tax_amount)} readOnly />
                                </label>
                                <label>
                                    <span>Discount</span>
                                    <input value={fmtMoney(selectedOrder.discount_amount)} readOnly />
                                </label>
                                <label>
                                    <span>Total</span>
                                    <input value={fmtMoney(selectedOrder.total_amount)} readOnly />
                                </label>
                            </div>

                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
